import { useMemo, useState, type FormEvent } from 'react';
import type React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Mail,
  MessageSquareText,
} from 'lucide-react';
import { DiscloserMark } from '@/components/Logo';
import { supabase } from '@/lib/supabase';

const LEAD_MAGNET_SLUG = 'ca-disclosure-preflight-90-second';

const PREFLIGHT_ITEMS = [
  {
    label: 'Missing forms',
    detail: 'If the TDS, SPQ, AVID, NHD, HOA packet, or lead form is missing, stop there first.',
  },
  {
    label: 'Y / Unknown flags',
    detail: 'Start with every Yes and Unknown. That is where most follow-up lives.',
  },
  {
    label: 'Contradictions',
    detail: 'Seller says unknown. The AVID, NHD, or inspection says something else.',
  },
  {
    label: 'Repair exposure',
    detail: 'Anything that could become a credit request, contractor call, or late-stage delay.',
  },
  {
    label: 'Buyer questions',
    detail: 'The questions your buyer will text after page 12, written before they ask.',
  },
  {
    label: 'Seller clarification',
    detail: 'The short email to send before the buyer side catches the issue first.',
  },
  {
    label: 'Audit trail',
    detail: 'What needs a timestamp if the file gets questioned later.',
  },
];

const SAMPLE_FINDINGS = [
  {
    severity: 'Clarify',
    title: 'Seller marked "unknown." AVID notes a visible seismic strap issue.',
    citation: 'SPQ II.A.10 + AVID Section C.1',
  },
  {
    severity: 'Buyer Q',
    title: 'Water-heater language looks scary. This part is standard CA compliance text.',
    citation: 'TDS II.A.6',
  },
  {
    severity: 'Follow-up',
    title: 'Fire-zone disclosure should be acknowledged before offer review.',
    citation: 'NHD III.2.A',
  },
];

function collectUtm(searchParams: URLSearchParams) {
  return {
    utm_source: searchParams.get('utm_source'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_content: searchParams.get('utm_content'),
    utm_medium: searchParams.get('utm_medium'),
    utm_term: searchParams.get('utm_term'),
  };
}

export default function LandingScan() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('agent');
  const [company, setCompany] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);

  const onboardingHref = useMemo(() => {
    const params = new URLSearchParams({
      source: 'preflight',
      lead_magnet: LEAD_MAGNET_SLUG,
    });
    if (capturedEmail) params.set('email', capturedEmail);
    return `/onboarding?${params.toString()}`;
  }, [capturedEmail]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('Enter a valid email.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: captureError } = await supabase.functions.invoke('lead-capture', {
        body: {
          email: cleanEmail,
          role,
          company,
          source: 'preflight',
          lead_magnet_slug: LEAD_MAGNET_SLUG,
          requested_asset: 'preflight-checklist-and-sample-confidence-report',
          page_path:
            typeof window !== 'undefined'
              ? `${window.location.pathname}${window.location.search}`
              : '/scan',
          referrer: typeof document !== 'undefined' ? document.referrer : null,
          metadata: {
            funnel_step: 'lead_magnet_capture',
            offer: '90-second California Disclosure Pre-Flight',
          },
          ...collectUtm(searchParams),
        },
      });

      if (captureError) {
        console.error('lead-capture failed', captureError);
      }
    } catch (err) {
      console.error('lead-capture failed', err);
    } finally {
      setCapturedEmail(cleanEmail);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-paper-warm text-brand-ink">
      <header className="border-b border-brand-ink/[0.06] bg-white/70 backdrop-blur">
        <div className="max-w-[1120px] mx-auto px-5 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <DiscloserMark size={18} />
            <span className="font-display font-semibold text-[18px] tracking-tight">
              Discloser
            </span>
          </Link>
          <Link
            to="/auth"
            className="font-meta text-[12px] uppercase tracking-[0.14em] text-brand-text-3 hover:text-brand-ink"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main>
        <section className="bg-gradient-to-b from-brand-sky/30 via-brand-cream/20 to-transparent">
          <div className="max-w-[1120px] mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-12 md:pb-16 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-pill bg-white/80 border border-brand-ink/[0.08] px-3.5 h-7 mb-7">
                <DiscloserMark size={12} />
                <span className="font-meta text-[11px] uppercase tracking-[0.16em] text-brand-text-3">
                  Free pre-flight for California packets
                </span>
              </div>

              <h1 className="text-[40px] md:text-[58px] lg:text-[68px] leading-[0.98] tracking-[-0.03em] mb-6 max-w-[760px]">
                <span className="block font-brand font-semibold tracking-[-0.025em]">
                  Before you read
                </span>
                <span className="block font-display italic font-semibold tracking-[-0.025em] mt-1 text-brand-accent">
                  300 pages.
                </span>
              </h1>

              <p className="font-brand text-[17px] md:text-[19px] text-brand-text-2 max-w-[650px] leading-[1.55] mb-7">
                Run the 90-second pre-flight. Find the missing forms, Yes/Unknown
                answers, and contradictions most likely to turn into buyer questions,
                seller emails, or E&O pain.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[700px]">
                <ProofChip icon={ClipboardCheck} label="7 checks before the long read" />
                <ProofChip icon={MessageSquareText} label="Buyer questions to expect" />
                <ProofChip icon={FileText} label="Sample Closer report" />
              </div>
            </div>

            <div className="bg-white border border-brand-ink/[0.08] rounded-2xl p-5 md:p-6 shadow-[0_18px_60px_-38px_rgba(26,22,21,0.35)]">
              {capturedEmail ? (
                <SuccessPanel onboardingHref={onboardingHref} />
              ) : (
                <form onSubmit={handleSubmit} aria-label="Get the disclosure pre-flight">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-4 h-4 text-brand-accent" />
                    <h2 className="font-brand text-[22px] font-semibold tracking-[-0.02em]">
                      Send me the pre-flight
                    </h2>
                  </div>

                  <p className="font-brand text-[14px] text-brand-text-2 leading-[1.5] mb-5">
                    Enter your email. The checklist opens here. So does a sample
                    Confidence Report. Then you can upload one real packet free.
                  </p>

                  <label className="block mb-3">
                    <span className="font-meta text-[11px] uppercase tracking-[0.14em] text-brand-text-3 mb-1.5 block">
                      Work email
                    </span>
                    <input
                      type="email"
                      required
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@brokerage.com"
                      className="w-full h-11 rounded-md border border-brand-ink/15 bg-brand-paper-warm px-3 font-brand text-[15px] text-brand-ink placeholder:text-brand-text-3 focus:outline-none focus:border-brand-ink/40"
                    />
                  </label>

                  <label className="block mb-4">
                    <span className="font-meta text-[11px] uppercase tracking-[0.14em] text-brand-text-3 mb-1.5 block">
                      I am a
                    </span>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="w-full h-11 rounded-md border border-brand-ink/15 bg-brand-paper-warm px-3 font-brand text-[15px] text-brand-ink focus:outline-none focus:border-brand-ink/40"
                    >
                      <option value="agent">Real estate agent</option>
                      <option value="broker">Broker / team lead</option>
                      <option value="tc">Transaction coordinator</option>
                      <option value="buyer">Buyer / client</option>
                    </select>
                  </label>

                  <label className="hidden" aria-hidden="true">
                    Company
                    <input
                      tabIndex={-1}
                      autoComplete="off"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </label>

                  {error && (
                    <p role="alert" className="font-brand text-[13px] text-brand-accent mb-3">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 rounded-pill bg-brand-ink text-white font-brand text-[15px] font-semibold h-12 px-[22px] w-full transition-colors hover:bg-brand-ink-soft active:scale-[0.97] disabled:opacity-60"
                  >
                    {submitting ? 'Opening pre-flight...' : 'Get the 90-second pre-flight'}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <p className="font-meta text-[11px] text-brand-text-3 mt-3 leading-[1.5]">
                    Free. No card. Built for California disclosure packets, not
                    generic real estate leads.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="max-w-[1120px] mx-auto px-5 md:px-8 py-10 md:py-16 grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <ChecklistPanel />
          <ConfidenceReportPreview />
        </section>

        <section className="border-t border-brand-ink/[0.06]">
          <div className="max-w-[1120px] mx-auto px-5 md:px-8 py-10 md:py-14 grid gap-7 md:grid-cols-3">
            <StepCard n={1} title="Run the pre-flight">
              Check the packet for the seven places deals usually start to wobble.
            </StepCard>
            <StepCard n={2} title="Know what to ask">
              See the buyer questions and seller clarifications before they hit your inbox.
            </StepCard>
            <StepCard n={3} title="Upload the real packet">
              Let Closer do the same work with citations, severity, and a clean trail.
            </StepCard>
          </div>
        </section>

        <section className="max-w-[1120px] mx-auto px-5 md:px-8 py-12 md:py-16 border-t border-brand-ink/[0.06]">
          <div className="max-w-[760px]">
            <h2 className="font-brand text-[30px] md:text-[44px] font-semibold tracking-[-0.025em] mb-4">
              A document room stores the packet.
            </h2>
            <p className="font-brand text-[16px] md:text-[17px] text-brand-text-2 leading-[1.6] mb-7">
              Discloser reads it, explains it, and helps both sides move before the
              deal loses momentum. If you just need folders, this is too much tool.
              If you need to know what the packet means, start here.
            </p>
            <Link
              to={onboardingHref}
              className="inline-flex items-center justify-center gap-2 rounded-pill bg-brand-ink text-white font-brand text-[15px] font-semibold h-12 px-[22px] hover:bg-brand-ink-soft active:scale-[0.97]"
            >
              Upload one packet free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <footer className="max-w-[1120px] mx-auto px-5 md:px-8 py-8 font-meta text-[11px] text-brand-text-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <span>Discloser - Built for California disclosure packets</span>
          <span>
            <Link to="/privacy" className="hover:text-brand-ink mr-4">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-brand-ink">
              Terms
            </Link>
          </span>
        </footer>
      </main>
    </div>
  );
}

function ProofChip({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-pill bg-white/75 border border-brand-ink/[0.08] px-3.5 h-10 font-meta text-[12px] text-brand-text-2">
      <Icon className="w-3.5 h-3.5 text-brand-accent" />
      {label}
    </div>
  );
}

function SuccessPanel({ onboardingHref }: { onboardingHref: string }) {
  return (
    <div>
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-info-bg mb-4">
        <CheckCircle2 className="w-5 h-5 text-brand-info" />
      </div>
      <h2 className="font-brand text-[24px] font-semibold tracking-[-0.02em] mb-2">
        You got it.
      </h2>
      <p className="font-brand text-[14px] text-brand-text-2 leading-[1.5] mb-5">
        Start with the checklist below. Then upload a real packet and Closer will
        return the findings with citations.
      </p>
      <div className="flex flex-col gap-3">
        <a
          href="#checklist"
          className="inline-flex items-center justify-center gap-2 rounded-pill bg-white border border-brand-ink/10 text-brand-ink font-brand text-[14px] font-semibold h-11 px-5 hover:border-brand-ink/25"
        >
          View checklist <ClipboardCheck className="w-4 h-4" />
        </a>
        <Link
          to={onboardingHref}
          className="inline-flex items-center justify-center gap-2 rounded-pill bg-brand-ink text-white font-brand text-[14px] font-semibold h-11 px-5 hover:bg-brand-ink-soft"
        >
          Upload one packet free <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function ChecklistPanel() {
  return (
    <section id="checklist" className="bg-white border border-brand-ink/[0.08] rounded-2xl p-5 md:p-6">
      <div className="flex items-center gap-2 mb-5">
        <ClipboardCheck className="w-4 h-4 text-brand-accent" />
        <h2 className="font-brand text-[24px] md:text-[28px] font-semibold tracking-[-0.025em]">
          The 7 checks
        </h2>
      </div>
      <div className="space-y-3">
        {PREFLIGHT_ITEMS.map((item, index) => (
          <div
            key={item.label}
            className="grid grid-cols-[32px_1fr] gap-3 border-t border-brand-ink/[0.06] pt-3 first:border-t-0 first:pt-0"
          >
            <span className="font-display italic text-[22px] text-brand-accent leading-none">
              {index + 1}.
            </span>
            <div>
              <div className="font-brand text-[15px] font-semibold text-brand-ink">
                {item.label}
              </div>
              <div className="font-brand text-[13.5px] text-brand-text-2 leading-[1.45] mt-0.5">
                {item.detail}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ConfidenceReportPreview() {
  return (
    <section className="bg-brand-ink text-white rounded-2xl p-5 md:p-6 shadow-[0_18px_60px_-38px_rgba(26,22,21,0.45)]">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <div className="font-meta text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">
            What Closer returns
          </div>
          <h2 className="font-brand text-[24px] md:text-[28px] font-semibold tracking-[-0.025em]">
            Confidence Report preview
          </h2>
        </div>
        <DiscloserMark size={36} fill="white" />
      </div>

      <div className="rounded-xl bg-white/[0.06] border border-white/10 p-4 mb-4">
        <div className="font-meta text-[10px] uppercase tracking-[0.16em] text-white/45 mb-2">
          Sample packet readout
        </div>
        <p className="font-brand text-[14px] leading-[1.55] text-white/82">
          The checklist tells you what to look for. Closer does it against the real
          packet, cites the source, and drafts the next email.
        </p>
      </div>

      <div className="space-y-3">
        {SAMPLE_FINDINGS.map((finding) => (
          <div key={finding.title} className="rounded-xl bg-white border border-white/10 p-4 text-brand-ink">
            <div className="flex items-center gap-2 mb-2">
              <span className="rounded bg-brand-accent/12 px-2 py-0.5 font-meta text-[10px] uppercase tracking-[0.12em] text-brand-accent font-semibold">
                {finding.severity}
              </span>
              <span className="font-meta text-[10px] uppercase tracking-[0.12em] text-brand-text-3">
                {finding.citation}
              </span>
            </div>
            <div className="font-brand text-[14.5px] font-semibold leading-snug">
              {finding.title}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StepCard({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-brand-ink/[0.08] rounded-2xl p-5">
      <div className="w-8 h-8 rounded-full bg-brand-paper flex items-center justify-center font-display italic font-semibold text-brand-accent mb-4">
        {n}
      </div>
      <h3 className="font-brand text-[18px] font-semibold tracking-[-0.015em] mb-2">
        {title}
      </h3>
      <p className="font-brand text-[14px] text-brand-text-2 leading-[1.5]">
        {children}
      </p>
    </div>
  );
}
